import React, { useState } from 'react';
import { FileText, AlertTriangle, CheckCircle2, Info, Lightbulb, ArrowRight, BookOpen, Quote, ShieldAlert, Globe, BadgeCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, GlassCard } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScoreRing, Progress } from '@/components/ui/progress';
import { runArticleAnalysis } from '@/lib/mlClient';
import { MLArticleInsights, PlainSummary, MLReasoning } from './MLInsights';
import { explainArticle } from '@/lib/plainExplanation';
import { isTrustedSource, parseDomain } from '@/lib/trustedSources';
import { useApp } from '@/context/AppContext';
import { cn } from '@/lib/utils';

// Responsive Score Ring
const CredibilityScoreRing = ({ score, size = 140, mobileSize = 120, strokeWidth = 12 }) => {
  const radius = (size - strokeWidth) / 2;
  const mobileRadius = (mobileSize - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const mobileCircumference = mobileRadius * 2 * Math.PI;
  const offset = circumference - (score / 100) * circumference;
  const mobileOffset = mobileCircumference - (score / 100) * mobileCircumference;
  
  const getColor = () => {
    if (score >= 80) return "hsl(var(--success))";
    if (score >= 50) return "hsl(var(--warning))";
    return "hsl(var(--destructive))";
  };

  const getLabel = () => {
    if (score >= 80) return "High";
    if (score >= 50) return "Mixed";
    return "Low";
  };

  return (
    <div className="relative inline-flex items-center justify-center">
      {/* Mobile Ring */}
      <svg width={mobileSize} height={mobileSize} className="transform -rotate-90 sm:hidden">
        <circle cx={mobileSize / 2} cy={mobileSize / 2} r={mobileRadius} fill="none" stroke="hsl(var(--muted))" strokeWidth={strokeWidth} />
        <circle cx={mobileSize / 2} cy={mobileSize / 2} r={mobileRadius} fill="none" stroke={getColor()} strokeWidth={strokeWidth} strokeDasharray={mobileCircumference} strokeDashoffset={mobileOffset} strokeLinecap="round" className="transition-all duration-700 ease-out" />
      </svg>
      
      {/* Desktop Ring */}
      <svg width={size} height={size} className="transform -rotate-90 hidden sm:block">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="hsl(var(--muted))" strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={getColor()} strokeWidth={strokeWidth} strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" className="transition-all duration-700 ease-out" />
      </svg>
      
      <div className="absolute flex flex-col items-center justify-center">
        <span className="text-2xl sm:text-3xl font-bold font-display" style={{ color: getColor() }}>{score}</span>
        <span className="text-xs text-muted-foreground font-medium">{getLabel()}</span>
      </div>
    </div>
  );
};

// Credibility Score Display Component
const CredibilityScoreDisplay = ({ result }) => {
  const getCategoryConfig = () => {
    switch (result.category) {
      case 'High Credibility':
        return { color: 'text-success', bg: 'bg-success/10', border: 'border-success/30', badge: 'safe', icon: CheckCircle2 };
      case 'Mixed / Uncertain':
        return { color: 'text-warning', bg: 'bg-warning/10', border: 'border-warning/30', badge: 'suspicious', icon: AlertTriangle };
      case 'Low Credibility':
        return { color: 'text-destructive', bg: 'bg-destructive/10', border: 'border-destructive/30', badge: 'danger', icon: ShieldAlert };
      default:
        return { color: 'text-muted-foreground', bg: 'bg-muted', border: 'border-border', badge: 'unknown', icon: Info };
    }
  };

  const config = getCategoryConfig();
  const Icon = config.icon;

  return (
    <GlassCard className={cn("p-4 sm:p-6", config.border, "border-2")}>
      {/* Mobile: Stacked centered, Desktop: Side by side */}
      <div className="flex flex-col items-center text-center lg:flex-row lg:items-start lg:text-left gap-4 lg:gap-6">
        <div className="flex-shrink-0">
          <CredibilityScoreRing score={result.credibilityScore} />
        </div>
        
        <div className="flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-2 justify-center lg:justify-start">
            <Badge variant={config.badge} className="text-sm">
              <Icon className="w-4 h-4 mr-1" />
              {result.category}
            </Badge>
            {result.hardFailureSignals && result.hardFailureSignals.length > 0 && (
              <Badge variant="danger" className="text-xs">
                {result.hardFailureSignals.length} Critical
              </Badge>
            )}
          </div>
          
          <div>
            <h3 className="font-display text-lg sm:text-xl font-bold text-foreground">
              Article Credibility Score
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {result.credibilityScore >= 80 
                ? "This article appears credible and well-sourced."
                : result.credibilityScore >= 50
                ? "Mixed signals detected. Cross-reference with trusted sources."
                : "Multiple critical issues detected. Exercise extreme caution."}
            </p>
          </div>

          {/* Credibility Bar */}
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-xs text-muted-foreground">Credibility Level</span>
              <span className="text-xs font-mono">
                <span className={cn("font-bold", config.color)}>{result.credibilityScore}</span>
                <span className="text-muted-foreground">/100</span>
              </span>
            </div>
            <div className="relative h-3 sm:h-4 w-full overflow-hidden rounded-full bg-muted">
              <div
                className={cn(
                  "h-full transition-all duration-500 ease-out rounded-full",
                  result.credibilityScore >= 80 ? 'bg-success' :
                  result.credibilityScore >= 50 ? 'bg-warning' : 'bg-destructive'
                )}
                style={{ width: `${result.credibilityScore}%` }}
              />
            </div>
            <div className="flex justify-between text-[10px] sm:text-xs text-muted-foreground mt-1">
              <span>Low (0-49)</span>
              <span>Mixed (50-79)</span>
              <span>High (80-100)</span>
            </div>
          </div>
        </div>
      </div>
    </GlassCard>
  );
};

// Risk Details Component
const RiskDetails = ({ result }) => {
  return (
    <div className="space-y-4">
      {/* Hard Rule Override Alert */}
      {result.hardRuleOverride && result.hardRuleTriggered && (
        <div className="p-4 rounded-xl bg-destructive/10 border-2 border-destructive/30">
          <div className="flex items-start gap-2">
            <ShieldAlert className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
            <div>
              <span className="font-bold text-sm text-destructive">HARD RULE OVERRIDE</span>
              <p className="text-xs sm:text-sm text-destructive/90 mt-1">{result.hardRuleTriggered}</p>
            </div>
          </div>
        </div>
      )}

      {/* Analysis Summary */}
      <GlassCard className="p-4 sm:p-5">
        <h4 className="text-sm font-semibold mb-3">Analysis Summary</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="text-center p-3 rounded-lg bg-muted/50">
            <div className={cn("text-xl sm:text-2xl font-bold font-display", result.summary.conspiracyCount > 0 ? "text-destructive" : "text-success")}>
              {result.summary.conspiracyCount || 0}
            </div>
            <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">Conspiracy</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-muted/50">
            <div className={cn("text-xl sm:text-2xl font-bold font-display", result.summary.emotionalCount > 0 ? "text-warning" : "text-success")}>
              {result.summary.emotionalCount || 0}
            </div>
            <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">Emotional</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-muted/50">
            <div className={cn("text-xl sm:text-2xl font-bold font-display", result.summary.sourceCount > 0 ? "text-success" : "text-destructive")}>
              {result.summary.sourceCount}
            </div>
            <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">Sources</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-muted/50">
            <div className={cn("text-xl sm:text-2xl font-bold font-display", result.summary.expertCount > 0 ? "text-success" : "text-muted-foreground")}>
              {result.summary.expertCount || 0}
            </div>
            <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">Experts</p>
          </div>
        </div>
      </GlassCard>

      {/* Top Penalties */}
      {result.topReasons && result.topReasons.length > 0 && (
        <GlassCard className="p-4 sm:p-5">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-warning" />
            Top 5 Penalties Applied
          </h4>
          <div className="space-y-2">
            {result.topReasons.map((flag, index) => (
              <div 
                key={index}
                className={cn(
                  "p-3 rounded-lg border",
                  flag.isHardFailure ? 'bg-destructive/10 border-destructive/30' :
                  flag.severity === 'high' || flag.severity === 'critical' ? 'bg-destructive/10 border-destructive/30' :
                  flag.severity === 'medium' ? 'bg-warning/10 border-warning/30' :
                  'bg-muted/50 border-border'
                )}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-5 h-5 rounded-full bg-background flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {index + 1}
                    </span>
                    <span className="font-medium text-xs sm:text-sm truncate">{flag.title}</span>
                    {flag.isHardFailure && (
                      <Badge variant="danger" className="text-[10px] flex-shrink-0">HARD</Badge>
                    )}
                  </div>
                  <span className="text-xs font-mono text-destructive flex-shrink-0">-{flag.penalty}</span>
                </div>
                <p className="text-xs text-muted-foreground ml-7">{flag.description}</p>
                {flag.examples && flag.examples.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2 ml-7">
                    {flag.examples.slice(0, 3).map((ex, i) => (
                      <span key={i} className="px-2 py-0.5 rounded bg-background/80 text-[10px] font-mono">"{ex}"</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {/* Positive Signals */}
      {result.flags && result.flags.filter(f => f.type === 'positive').length > 0 && (
        <GlassCard className="p-4 sm:p-5">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-success" />
            Credibility Bonuses
          </h4>
          <div className="space-y-2">
            {result.flags.filter(f => f.type === 'positive').map((flag, index) => (
              <div key={index} className="p-3 rounded-lg border bg-success/10 border-success/30">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-xs sm:text-sm text-success">{flag.title}</span>
                  <span className="text-xs font-mono text-success">+{flag.bonus}</span>
                </div>
                <p className="text-xs text-muted-foreground">{flag.description}</p>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {/* Recommendations */}
      <GlassCard className="p-4 sm:p-5">
        <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-primary" />
          Recommendations
        </h4>
        <ul className="space-y-2">
          {result.recommendations.map((rec, index) => (
            <li key={index} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
              <ArrowRight className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <span>{rec}</span>
            </li>
          ))}
        </ul>
      </GlassCard>
    </div>
  );
};

export const ArticleVerification = () => {
  const [text, setText] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { addVerification } = useApp();

  const trustedHint = sourceUrl.trim() ? isTrustedSource(sourceUrl) : false;
  const parsedHost = sourceUrl.trim() ? parseDomain(sourceUrl) : '';

  const handleVerify = async () => {
    if (!text.trim()) return;
    
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const verificationResult = await runArticleAnalysis(text, sourceUrl);
    setResult(verificationResult);
    
    addVerification({
      type: 'article',
      input: (sourceUrl.trim() ? `[${parseDomain(sourceUrl) || 'source'}] ` : '') + text.substring(0, 100) + (text.length > 100 ? '...' : ''),
      category: verificationResult.category,
      credibilityScore: verificationResult.credibilityScore,
      flagCount: verificationResult.flags.filter(f => f.type !== 'positive').length,
      hardRuleOverride: verificationResult.hardRuleOverride,
      result: {
        sourceUrl: verificationResult.sourceUrl,
        sourceDomain: verificationResult.sourceDomain,
        trustedSource: verificationResult.trustedSource,
        category: verificationResult.category,
      },
    });
    
    setIsLoading(false);
  };

  const exampleTexts = [
    {
      label: 'Fake news',
      text: "SHOCKING: Insiders claim the government is HIDING the truth about 5G! Sources say 87% of scientists are being suppressed. Act now before it's too late! They don't want you to know this devastating secret!",
    },
    {
      label: 'Credible',
      text: "According to a peer-reviewed study published in Nature Medicine, researchers at Stanford University found that regular exercise can improve cognitive function. The research, conducted over five years by Dr. Sarah Chen, indicates that participants who exercised three times weekly showed measurable improvements in memory tests. However, experts say more research is needed to confirm these findings.",
    },
  ];

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 sm:gap-3 mb-2">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl gradient-secondary flex items-center justify-center">
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-secondary-foreground" />
          </div>
          <h2 className="font-display text-xl sm:text-2xl font-bold text-foreground">Article Verification</h2>
        </div>
        <p className="text-sm sm:text-base text-muted-foreground">
          Analyze article text for misinformation and credibility issues.
        </p>
      </div>

      {/* Desktop Two-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Input */}
        <div className="space-y-6">
          <GlassCard className="p-4 sm:p-6">
            <div className="space-y-4">
              {/* Optional Article Source URL */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5" />
                  Article Source URL <span className="text-muted-foreground/60">(optional)</span>
                </label>
                <Input
                  type="url"
                  inputMode="url"
                  placeholder="https://www.bbc.com/news/world-12345678"
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  className="h-10 text-sm"
                  data-testid="article-source-url-input"
                />
                {parsedHost && (
                  <div className="flex items-center gap-2 text-xs">
                    {trustedHint ? (
                      <span className="inline-flex items-center gap-1 text-success" data-testid="trusted-source-hint">
                        <BadgeCheck className="w-3.5 h-3.5" />
                        Trusted publisher: <span className="font-mono">{parsedHost}</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-muted-foreground" data-testid="unknown-source-hint">
                        <Info className="w-3.5 h-3.5" />
                        Unknown publisher: <span className="font-mono">{parsedHost}</span>
                      </span>
                    )}
                  </div>
                )}
              </div>

              <Textarea
                placeholder="Paste article text here to analyze..."
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="min-h-[160px] sm:min-h-[200px] text-base resize-none w-full"
                data-testid="article-input"
              />
              
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="text-xs sm:text-sm text-muted-foreground">
                  {text.length > 0 && (
                    <span>{text.split(/\s+/).filter(w => w).length} words</span>
                  )}
                </div>
                
                <Button 
                  onClick={handleVerify} 
                  disabled={text.trim().length < 50 || isLoading}
                  className="w-full sm:w-auto h-12 text-base"
                  variant="gradient"
                  data-testid="analyze-button"
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  ) : (
                    <>
                      <BookOpen className="w-5 h-5 mr-2" />
                      Analyze Content
                    </>
                  )}
                </Button>
              </div>

              {text.trim().length > 0 && text.trim().length < 50 && (
                <p className="text-xs text-warning">Minimum 50 characters required.</p>
              )}

              {/* Examples */}
              <div className="pt-4 border-t border-border/50">
                <p className="text-xs text-muted-foreground mb-2">Try examples:</p>
                <div className="flex flex-wrap gap-2">
                  {exampleTexts.map((example, index) => (
                    <button
                      key={index}
                      onClick={() => setText(example.text)}
                      className={cn(
                        "px-3 py-2 rounded-lg text-xs font-medium transition-colors min-h-[36px]",
                        example.label === 'Credible'
                          ? "bg-success/10 hover:bg-success/20 text-success"
                          : "bg-destructive/10 hover:bg-destructive/20 text-destructive"
                      )}
                    >
                      {example.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </GlassCard>

          {/* Desktop: Show Score in left column */}
          {result && (
            <div className="hidden lg:block">
              <CredibilityScoreDisplay result={result} />
            </div>
          )}

          {/* Info Cards - Only when no result */}
          {!result && (
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-4">
              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2 sm:pb-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-destructive/10 flex items-center justify-center mb-2">
                    <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-destructive" />
                  </div>
                  <CardTitle className="text-sm sm:text-base">Misinformation Detection</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-xs sm:text-sm">
                    Identifies emotional manipulation, conspiracy framing, and unsupported claims.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2 sm:pb-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-warning/10 flex items-center justify-center mb-2">
                    <Quote className="w-4 h-4 sm:w-5 sm:h-5 text-warning" />
                  </div>
                  <CardTitle className="text-sm sm:text-base">Source Analysis</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-xs sm:text-sm">
                    Checks for citations, expert references, and credible sources.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2 sm:pb-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-success/10 flex items-center justify-center mb-2">
                    <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-success" />
                  </div>
                  <CardTitle className="text-sm sm:text-base">Credibility Scoring</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-xs sm:text-sm">
                    0-100 score with hard rule override for critical issues.
                  </CardDescription>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Right Column: Results */}
        {result && (
          <div className="space-y-6">
            {/* Mobile: Show Score here */}
            <div className="lg:hidden">
              <CredibilityScoreDisplay result={result} />
            </div>
            
            <PlainSummary summary={explainArticle(result)} testId="article-plain-summary" />
            <RiskDetails result={result} />
            <MLReasoning testId="article-ml-toggle">
              <MLArticleInsights ml={result.ml} />
            </MLReasoning>
            <MLArticleInsights ml={result.ml} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticleVerification;
